import PushSettingsForm from "@/components/PushSettingsForm";
import PushProvidersConfig from "@/components/PushProvidersConfig";
import { PageHeader } from "@/components/Ui";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Send, Loader2, Users, Search, Bell } from "lucide-react";
import { api, apiError } from "@/lib/api";

// ── Client Hearing Reminder Manual Trigger ────────────────────────────────────
function ClientHearingTriggerCard() {
    const [tenants, setTenants] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [open, setOpen] = useState(false);
    const [deropStatus, setDeropoStatus] = useState(null); // null | {connected, message}

    useEffect(() => {
        api.get("/tenants")
            .then(r => setTenants(r.data || []))
            .catch(() => {});
        // Probe Deropo instance upfront
        api.get("/admin/push/deropo-status")
            .then(r => setDeropoStatus(r.data))
            .catch(() => {});
    }, []);

    const filtered = tenants.filter(t =>
        !search || (t.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.slug || "").toLowerCase().includes(search.toLowerCase())
    );

    const dispatch = async () => {
        const scope = selectedTenant ? `"${selectedTenant.name}"` : "all tenants";
        if (!window.confirm(`Send hearing-tomorrow WhatsApp + email to ALL clients with a hearing scheduled for tomorrow?\n\nScope: ${scope}\n\nIdempotent — each client gets at most one message per day.`)) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await api.post("/admin/push/trigger-hearing-client", {
                tenant_id: selectedTenant?.id || null,
            });
            setResult(r.data);
            if (r.data.sent === 0 && r.data.cases_found === 0) {
                toast.message("No hearings found for tomorrow", {
                    description: `Date window: ${r.data.date_window}`,
                    duration: 8000,
                });
            } else if (r.data.sent > 0) {
                toast.success(`Dispatched to ${r.data.sent} client(s)`, { duration: 8000 });
            } else {
                toast.message(`${r.data.cases_found} case(s) found — ${r.data.skipped} already sent today`, {
                    duration: 8000,
                });
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="bg-muted border border-border p-5 space-y-4" data-testid="client-hearing-trigger-card">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-xs text-primary uppercase tracking-[0.16em] mb-1 flex items-center gap-2">
                        <Bell className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        Client hearing reminders — Manual dispatch
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        Sends "hearing tomorrow" WhatsApp + email to every client linked to a case with a hearing scheduled for tomorrow (IST).
                        Automated dispatch runs at <strong>08:00 IST daily</strong>. This button lets you trigger it at any time.
                        Idempotent — each client gets at most one message per calendar day.
                    </div>
                </div>
            </div>

            {/* Deropo instance status banner */}
            {deropStatus !== null && !deropStatus.connected && (
                <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/30 text-[11px] text-orange-400" data-testid="deropo-disconnect-banner">
                    <span className="font-bold shrink-0">Deropo disconnected.</span>
                    <span>{deropStatus.message}.</span>
                    <a href="/admin/integrations#deropo" className="ml-auto shrink-0 underline hover:text-orange-300 whitespace-nowrap">
                        Reconnect →
                    </a>
                </div>
            )}
            {deropStatus?.connected && (
                <div className="text-[10px] text-green-500" data-testid="deropo-ok-badge">Deropo instance connected</div>
            )}

            {/* Tenant selector */}
            <div className="space-y-2">
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Scope</div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5}/>
                    <input
                        type="text"
                        value={search}
                        onChange={e => { setSearch(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 150)}
                        placeholder="Search tenant… (leave blank = all tenants)"
                        data-testid="client-trigger-tenant-search"
                        className="w-full bg-background border border-border pl-9 pr-4 py-2 text-sm focus:border-primary outline-none"
                    />
                </div>
                {open && filtered.length > 0 && (
                    <div className="border border-border bg-background shadow-lg max-h-48 overflow-y-auto z-50 relative">
                        <div
                            className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-muted flex items-center gap-2 ${!selectedTenant ? "text-primary" : "text-muted-foreground"}`}
                            onMouseDown={() => { setSelectedTenant(null); setSearch(""); setOpen(false); }}
                            data-testid="tenant-option-all">
                            <Users className="w-3.5 h-3.5" strokeWidth={1.5}/>
                            All tenants
                        </div>
                        {filtered.slice(0, 30).map(t => (
                            <div
                                key={t.id}
                                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-muted ${selectedTenant?.id === t.id ? "text-primary" : ""}`}
                                onMouseDown={() => { setSelectedTenant(t); setSearch(t.name); setOpen(false); }}
                                data-testid={`tenant-option-${t.id}`}>
                                {t.name}
                                {t.cases_count > 0 && <span className="ml-2 text-xs text-muted-foreground">{t.cases_count} cases</span>}
                            </div>
                        ))}
                    </div>
                )}
                <div className="text-[10px] text-muted-foreground">
                    {selectedTenant
                        ? <span>Scoped to: <strong className="text-foreground">{selectedTenant.name}</strong></span>
                        : <span>Scope: <strong className="text-foreground">All tenants</strong></span>
                    }
                </div>
            </div>

            <button
                onClick={dispatch}
                disabled={busy}
                data-testid="client-trigger-dispatch-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                {busy ? "Dispatching…" : "Dispatch hearing reminders to clients now"}
            </button>

            {result && (
                <div className="bg-background border border-border p-4 text-xs space-y-2" data-testid="client-trigger-result">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Last dispatch result</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            { label: "Cases found", val: result.cases_found, color: "" },
                            { label: "Sent", val: result.sent, color: "text-green-500" },
                            { label: "Already sent today", val: result.skipped, color: "text-muted-foreground" },
                            { label: "Errors", val: result.errors, color: result.errors > 0 ? "text-red-400" : "text-muted-foreground" },
                        ].map(({ label, val, color }) => (
                            <div key={label} className="text-center border border-border p-2">
                                <div className={`text-lg font-mono font-medium ${color}`}>{val}</div>
                                <div className="text-muted-foreground text-[10px]">{label}</div>
                            </div>
                        ))}
                    </div>
                    <div className="text-muted-foreground text-[10px]">Window: {result.date_window}</div>
                    {result.details?.length > 0 && (
                        <div className="space-y-1 max-h-40 overflow-y-auto border-t border-border pt-2 mt-2">
                            {/* Instance invalidated warning */}
                            {result.details.some(d => d.wa_reason?.includes("Invalidated") || d.wa_reason?.includes("invalidated") || d.wa_reason?.includes("Instance")) && (
                                <div className="mb-2 p-2 bg-orange-500/10 border border-orange-500/30 text-[11px] text-orange-400">
                                    <strong>Deropo instance disconnected.</strong> WhatsApp messages could not be delivered.{" "}
                                    <a href="/admin/integrations#deropo" className="underline hover:text-orange-300">
                                        Reconnect at Admin → Integrations
                                    </a>
                                    {" "}(scan QR code), then dispatch again.
                                </div>
                            )}
                            {result.details.slice(0, 15).map((d, i) => {
                                let waLabel, waColor;
                                if (d.whatsapp) {
                                    waLabel = "WA ✓"; waColor = "text-green-500";
                                } else if (d.wa_reason?.includes("Invalidated") || d.wa_reason?.includes("Instance")) {
                                    waLabel = "WA disconnected"; waColor = "text-orange-400";
                                } else if (d.wa_reason?.includes("no_phone") || d.wa_reason?.includes("invalid phone")) {
                                    waLabel = "WA no phone"; waColor = "text-yellow-500";
                                } else if (d.wa_reason) {
                                    waLabel = "WA ✗"; waColor = "text-red-400";
                                } else {
                                    waLabel = null;
                                }
                                return (
                                    <div key={i} className="flex items-center gap-2 text-[10px]">
                                        <span className="font-mono truncate flex-1">{d.case}</span>
                                        {waLabel && <span className={waColor}>{waLabel}</span>}
                                        {d.email && <span className="text-primary">Email ✓</span>}
                                    </div>
                                );
                            })}
                            {result.details.length > 15 && (
                                <div className="text-muted-foreground text-[10px]">+ {result.details.length - 15} more</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function AdminPushSettings() {
    const [busy, setBusy] = useState(false);
    const triggerNow = async () => {
        if (!window.confirm("Dispatch ALL of today's push notifications (hearing digests, cause-list digests, near-time reminders) right now?\n\nIdempotent — each user gets each notification at most once per day.")) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/push/trigger-today-now");
            const d = r.data?.diagnostic || {};
            if (r.data?.warning === "no_devices_registered") {
                toast.error(r.data.message || "0 devices registered", { duration: 12000 });
            } else if ((d.hearings_in_next_24h || 0) === 0 && (d.listings_today_ist || 0) === 0) {
                toast.message("Triggered — but no eligible hearings", {
                    description: `${d.devices_registered} devices registered. No hearings in next 24h or listings today.`,
                    duration: 10000,
                });
            } else {
                toast.success(r.data?.message || "Push dispatch queued.", { duration: 10000 });
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-8" data-testid="admin-push-page">
            <PageHeader title="Push notifications · Platform"
                subtitle="Default mobile push behaviour + delivery provider credentials. Tenants may override the behaviour per-firm."/>

            {/* Manual trigger — fires all today's pushes regardless of send-time window */}
            <div className="bg-muted border border-primary/40 p-4 flex flex-wrap items-center gap-3"
                data-testid="admin-push-manual-trigger-card">
                <div className="flex-1 min-w-0">
                    <div className="text-xs text-primary uppercase tracking-[0.16em] mb-1">Manual trigger</div>
                    <div className="text-[11px] text-muted-foreground">
                        Dispatch every scheduled push for today (hearing digest, cause-list digest, near-time reminders)
                        immediately. Safe to click multiple times — each user gets each notification at most once per day.
                    </div>
                </div>
                <button onClick={triggerNow} disabled={busy}
                    data-testid="admin-push-trigger-today-now"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                    {busy ? "Queuing…" : "Trigger today's pushes now"}
                </button>
            </div>

            {/* Client hearing reminder manual trigger */}
            <ClientHearingTriggerCard />

            <PushProvidersConfig />
            <div className="border-t border-border pt-8">
                <PushSettingsForm
                    scope="platform"
                    headline="Platform-wide push behaviour"
                    getEndpoint="/admin/push/settings"
                    putEndpoint="/admin/push/settings"
                    logsEndpoint="/admin/push/logs?limit=50"
                />
            </div>
        </div>
    );
}
